import { EMainProductCodeType } from '@aqx-core/common/api/config';
import { ESymbolCategory } from '@aqx-core/common/api/market';
import { EOrderType, IOrder } from '@aqx-core/common/api/order';
import { EColumnPreferenceOrderType } from '@aqx-core/common/api/user-account-setting';
import { ORDERS_MAX_SIZE } from '@aqx-core/common/constants/order';
import dayjs, { getSecondsSinceEpoch } from '@aqx-core/common/dayjs';
import { i18n } from '@aqx-core/common/i18n';

import { EDateRangeOption, ESortOption, IAssetsFilterValues } from '../assets-filter/types';
import { companyViewController } from '../company/controller';
import { DefaultFilterSortValue } from './constants';
import { EDeleteOrderType, IOrderHistoryDateRange, ITimeKeyFilterPosition } from './types';

export const getDefaultHistoryDateRange = (): IOrderHistoryDateRange => {
  return {
    to: dayjs().endOf('day').toDate(),
    from: dayjs().subtract(1, 'months').startOf('day').toDate()
  };
};

export const getMappedOpenPositionsKeyByIntegration = (
  integrationType: EMainProductCodeType | undefined,
  order: IOrder
) => (integrationType === EMainProductCodeType.HORIZON ? order.symbol : order.orderId);

export const getOrderHistoryTabKeyByIntegration = (isHorizon: boolean, isMT4: boolean) =>
  isHorizon || isMT4 ? EColumnPreferenceOrderType.ORDER_HISTORY : EColumnPreferenceOrderType.POSITION_HISTORY;

export const getStopOutTabByIntegration = (integrationType: EMainProductCodeType | undefined) => {
  if (integrationType === EMainProductCodeType.CENTROID) {
    return EColumnPreferenceOrderType.TRANSACTION_HISTORY;
  }
  if (integrationType === EMainProductCodeType.METATRADER5) {
    return EColumnPreferenceOrderType.POSITION_HISTORY;
  }
  return EColumnPreferenceOrderType.ORDER_HISTORY;
};

export const mapDeletePendingOrderHeaderDescription = (deleteType: EDeleteOrderType) => {
  const MESSAGE_MAP = {
    [EDeleteOrderType.ALL]: () =>
      i18n.t('Note: A maximum of {{orderMaxSize}} pending orders will be deleted.', {
        orderMaxSize: ORDERS_MAX_SIZE
      }),
    [EDeleteOrderType.LIMIT]: () =>
      i18n.t('Note: A maximum of {{orderMaxSize}} limit orders will be deleted.', {
        orderMaxSize: ORDERS_MAX_SIZE
      }),
    [EDeleteOrderType.STOP]: () =>
      i18n.t('Note: A maximum of {{orderMaxSize}} stop orders will be deleted.', {
        orderMaxSize: ORDERS_MAX_SIZE
      }),
    [EDeleteOrderType.STOP_LIMIT]: () =>
      i18n.t('Note: A maximum of {{orderMaxSize}} stop limit orders will be deleted.', {
        orderMaxSize: ORDERS_MAX_SIZE
      })
  };
  return MESSAGE_MAP[deleteType]();
};

export const mapDeletePendingOrderConfirmMessage = (deleteType: EDeleteOrderType) => {
  const MESSAGE_MAP = {
    [EDeleteOrderType.ALL]: () => i18n.t('Would you like to delete all pending orders?'),
    [EDeleteOrderType.LIMIT]: () => i18n.t('Would you like to delete all limit orders?'),
    [EDeleteOrderType.STOP]: () => i18n.t('Would you like to delete all stop orders?'),
    [EDeleteOrderType.STOP_LIMIT]: () => i18n.t('Would you like to delete all stop limit orders?')
  };
  return MESSAGE_MAP[deleteType]();
};

export const mapDeletePendingOrderSuccessMessage = (deleteType: EDeleteOrderType) => {
  const MESSAGE_MAP = {
    [EDeleteOrderType.ALL]: () => i18n.t('All pending orders have been deleted.'),
    [EDeleteOrderType.LIMIT]: () => i18n.t('All limit orders have been deleted.'),
    [EDeleteOrderType.STOP]: () => i18n.t('All stop orders have been deleted.'),
    [EDeleteOrderType.STOP_LIMIT]: () => i18n.t('All stop limit orders have been deleted.')
  };
  return MESSAGE_MAP[deleteType]();
};

export const convertDateRangeWithUtcOffset = (dateRange: IOrderHistoryDateRange) => {
  const convertedFrom = companyViewController
    .convertToDayjsWithOffset({
      input: dateRange.from,
      keepTime: true,
      baseFromOffset: true
    })
    .startOf('day');

  const convertedTo = companyViewController
    .convertToDayjsWithOffset({
      input: dateRange.to,
      keepTime: true,
      baseFromOffset: true
    })
    .endOf('day');

  const epochFrom = getSecondsSinceEpoch(convertedFrom);
  const epochTo = getSecondsSinceEpoch(convertedTo);

  return {
    epochFrom,
    epochTo
  };
};

export const filterPositionsByQuery = <T extends { symbol?: string }>(positions: T[], searchText: string): T[] => {
  if (!searchText) {
    return positions;
  }
  return positions.filter((item) => item.symbol?.toLowerCase().includes(searchText));
};

export const applyFilterPositions = <
  T extends { openTime?: number; closeTime?: number; orderType?: EOrderType; symbolType?: ESymbolCategory }
>(
  positions: T[],
  filterValues: IAssetsFilterValues | undefined,
  timeKey: ITimeKeyFilterPosition
): T[] => {
  if (!filterValues) {
    return positions;
  }

  const activeOrderType =
    filterValues.orderType !== undefined && filterValues.orderType !== 'ALL' ? filterValues.orderType : undefined;

  const activeAssetTypes =
    filterValues.assetTypes?.length &&
    !(filterValues.assetTypes.length === 1 && filterValues.assetTypes[0] === ESymbolCategory.ALL)
      ? filterValues.assetTypes
      : undefined;

  const activeCurrentAssetType =
    filterValues.currentAssetType && filterValues.currentAssetType !== ESymbolCategory.ALL
      ? filterValues.currentAssetType
      : undefined;

  const activeDateRange =
    filterValues.dateRange && filterValues.dateRange.value !== EDateRangeOption.ALL
      ? {
          startTime: filterValues.dateRange.from?.getTime(),
          endTime: filterValues.dateRange.to?.getTime()
        }
      : undefined;

  return positions.filter((item) => {
    if (activeOrderType !== undefined && item.orderType !== activeOrderType) {
      return false;
    }

    if (activeAssetTypes && (!item.symbolType || !activeAssetTypes.includes(item.symbolType))) {
      return false;
    }

    if (activeCurrentAssetType && item.symbolType !== activeCurrentAssetType) {
      return false;
    }

    if (activeDateRange) {
      const time = item[timeKey];
      if (!time) {
        return false;
      }
      if (activeDateRange.startTime && time < activeDateRange.startTime) {
        return false;
      }
      if (activeDateRange.endTime && time > activeDateRange.endTime) {
        return false;
      }
    }

    return true;
  });
};

export const applySortPositions = <T extends { orderId?: string; symbol?: string; profit?: number }>(
  positions: T[],
  sortBy: ESortOption | undefined
): T[] => {
  if (!sortBy) {
    return positions;
  }

  const list = [...positions];

  switch (sortBy) {
    case ESortOption.LATEST:
      // We need to convert orderId to number because backend returns orderId as string
      return list.sort((a, b) => (Number(b.orderId) || 0) - (Number(a.orderId) || 0));

    case ESortOption.OLDEST:
      return list.sort((a, b) => (Number(a.orderId) || 0) - (Number(b.orderId) || 0));

    case ESortOption.A_Z:
      return list.sort((a, b) => (a.symbol ?? '').localeCompare(b.symbol ?? ''));

    case ESortOption.Z_A:
      return list.sort((a, b) => (b.symbol ?? '').localeCompare(a.symbol ?? ''));

    case ESortOption.PROFIT_LOSS_ASCENDING:
      return list.sort((a, b) => (a.profit ?? 0) - (b.profit ?? 0));

    case ESortOption.PROFIT_LOSS_DESCENDING:
      return list.sort((a, b) => (b.profit ?? 0) - (a.profit ?? 0));

    default:
      return list;
  }
};

export const applyFilterAndSortPositions = <
  T extends {
    openTime?: number;
    closeTime?: number;
    orderType?: EOrderType;
    symbolType?: ESymbolCategory;
    symbol?: string;
    orderId?: string;
  }
>(
  positions: T[],
  searchText: string,
  filterValues: IAssetsFilterValues | undefined,
  timeKey: ITimeKeyFilterPosition
): T[] => {
  const filterBySearchText = filterPositionsByQuery(positions, searchText);
  return applySortPositions(applyFilterPositions(filterBySearchText, filterValues, timeKey), filterValues?.sortBy);
};

// eslint-disable-next-line complexity
export const getActiveFilterState = (filterValues: IAssetsFilterValues) => {
  const isFormHasSortByValues = filterValues?.sortBy !== DefaultFilterSortValue.sortBy;

  const isAssetTypesFilterActive = filterValues?.assetTypes?.length !== DefaultFilterSortValue.assetTypes?.length;
  const isAssetTypeFilterNotIsAll =
    filterValues?.assetTypes?.length === 1 && filterValues?.assetTypes[0] !== DefaultFilterSortValue.assetTypes?.[0];
  const isDateRangeFilterActive = filterValues?.dateRange?.value !== DefaultFilterSortValue.dateRange?.value;
  const isOrderTypeFilterActive = filterValues?.orderType !== DefaultFilterSortValue.orderType;

  const isFormHasFilterByValues = [
    isAssetTypesFilterActive,
    isAssetTypeFilterNotIsAll,
    isDateRangeFilterActive,
    isOrderTypeFilterActive
  ].includes(true);

  const isSubTabAssetTypeActive = filterValues?.currentAssetType !== DefaultFilterSortValue.currentAssetType;

  return {
    isActive: isFormHasFilterByValues || isFormHasSortByValues,
    isSortActive: isFormHasSortByValues,
    isFilterActive: isFormHasFilterByValues || isSubTabAssetTypeActive
  };
};
